import {Component, OnDestroy, OnInit} from '@angular/core';
import {WorkflowComponent} from "../../workflow.component";
import {ActivatedRoute, Router} from "@angular/router";
import {Model} from "../../../app.model";
import {observable, runInAction} from "mobx";
import {SaleOrder} from "../../../buy/buy.model";
import {firstValueFrom} from "rxjs";
import {BuyService, InstanceOfferData} from "../../../buy/buy.service";
import {WorkflowEndcustomerService} from "../workflow-endcustomer.service";
import {AffiliateService} from "../../../partner/affiliate/affiliate.service";
import {MessageService} from "primeng/api";
import {I18NextService} from "angular-i18next";
import {Subscription} from "../../../subscription/subscription.model";
import {SubscriptionService} from "../../../subscription/subscription.service";

interface WorkflowModel {
  saleOrder?: SaleOrder,
  isTrialNumberSelected?: boolean,
  instanceOffer?: InstanceOfferData,
}

@Component({
    selector: 'app-workflow-endcustomer-one-trial-create',
    templateUrl: './workflow-endcustomer-one-trial-create.component.html',
    standalone: false
})
export class WorkflowEndcustomerOneTrialCreateComponent extends WorkflowComponent implements OnInit, OnDestroy {
  @observable
  public model: WorkflowModel;

  private subscriptions: Subscription[];

  // If partner is ordering NFR for him self, he will order trial like end customer with additional info
  public isPartner: boolean;
  private canBuyTrialNumber: boolean;

  constructor(protected route: ActivatedRoute,
              private m: Model,
              protected router: Router,
              private buyService: BuyService,
              private endcustomerService: WorkflowEndcustomerService,
              private affiliateService: AffiliateService,
              private messageService: MessageService,
              private i18next: I18NextService,
              private subscriptionService: SubscriptionService) {
    super();
  }

  async ngOnInit() {
    super.ngOnInit();

    this.autorun(() => {
      this.isPartner = this.m.account.currentUser.companyIsPartner;
      this.canBuyTrialNumber = this.m.account.canBuyTrialNumber;
    });

    await this.initWorkflow();

    // Check if we already have a trial on this account.
    if (this.hasOneTrial()) {
      this.messageService.add({
        severity: 'error',
        summary: this.i18next.t('error') as string,
        detail: this.i18next.t('no_more_trial_allowed') as string
      });
      this.router.navigate(['/']);
      return;
    }

    if (this.isPartner) {
      this.navigate(['nfr'], true);
    } else if (this.canBuyTrialNumber) {
      this.navigate(['trial-phonenumber'], true);
    } else {
      this.navigate(['instance'], true);
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  private async initWorkflow() {
    // In the normal workflow, we will get a query like ?so=SO123456,
    // and we use query because we dont want to indicate a page reload after we get it as /SO123456
    const so = this.route.snapshot.queryParamMap.get('so');

    // We need the subscription to check if we have a trial on this account.
    if (!this.subscriptions) {
      try {
        this.subscriptions = await firstValueFrom(this.subscriptionService.getSubscriptions());
      } catch (e) {
        this.router.navigate(['/'], {replaceUrl: true});
        return;
      }
    }

    // Prepare the Model for this Workflow
    const model: WorkflowModel = {};

    // If we already have a sale order on our purchase model, we load it from the API.
    if (!this.m.buy.currentOrder?.name && so) {
      try {
        model.saleOrder = await firstValueFrom(this.buyService.getSaleOrder(so));
      } catch (e) {
        this.router.navigate(['/'], {replaceUrl: true});
        return;
      }
    }

    runInAction(() => {
      this.model = model
    });

    this.initialized = true;
  }

  public onNfrOnePrepareSelected() {
    if (this.canBuyTrialNumber) {
      this.navigate(['trial-phonenumber']);
      return;
    }
    this.navigate(['instance']);
  }

  public onNfrOnePrepareBack() {
    this.resetWorkflow();
  }

  public onTrialPhonenumberSelected(isSelected: boolean) {
    runInAction(() => {
      this.model.isTrialNumberSelected = isSelected;
    });

    this.navigate(['instance']);
  }

  public onTrialPhonenumberBack() {
    if (this.isPartner) {
      this.navigate(['nfr']);
      return;
    }
    this.resetWorkflow();
  }

  public async onInstanceNameSelected(instanceOfferData: InstanceOfferData) {
    runInAction(() => {
      this.model.instanceOffer = instanceOfferData;
    })

    await this.createOrder();
    this.affiliateService.refresh(true);
  }

  public onSelectInstanceNameBack() {
    if (this.canBuyTrialNumber) {
      this.navigate(['trial-phonenumber']);
      return;
    }

    if (this.isPartner) {
      this.navigate(['nfr']);
    } else {
      this.resetWorkflow();
    }
  }

  public onPhonenumberSelected(_: string) {
    this.router.navigate(['/do', 'trial', 'thank-you', this.model.saleOrder.name], {replaceUrl: true});
  }

  private async createOrder() {
    try {
      const saleOrder = await firstValueFrom(this.endcustomerService.oneTrialCreateOrder({
        instance_name: this.model.instanceOffer.name,
        acquire_phone_number: this.model.isTrialNumberSelected ? 'location_independent_number' : undefined
      }));
      runInAction(() => {
        this.model.saleOrder = saleOrder;
        this.m.buy.currentOrder = saleOrder;
      });
      if (this.model.isTrialNumberSelected) {
        this.navigate(['phonenumber'], true);
      } else {
        this.router.navigate(['/do', 'trial', 'thank-you', saleOrder.name], {replaceUrl: true});
      }
    } catch (e) {
      this.messageService.add({
        severity: 'error',
        summary: this.i18next.t('error') as string,
        detail: this.i18next.t('error_trial_create_sale_order') as string
      });
      throw e;
    }
  }

  private navigate(url: any[], replace = false) {
    this.router.navigate(['/do', 'one-trial', ...url], {
      replaceUrl: replace,
      queryParamsHandling: 'merge',
      preserveFragment: true,
      queryParams: {
        so: this.model?.saleOrder?.name ?? undefined,
      },
    });
  }

  private hasOneTrial() {
    return !!this.subscriptions.find((r: Subscription) => r.is_my && r.isOneTrial);
  }
}
